/** Shared, dependency-free dialog behavior for custom theme overlays. */
(function () {
  if (window.RaidageDialog) return;

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  class RaidageDialog {
    constructor(element, options = {}) {
      if (!element) throw new TypeError('RaidageDialog requires a dialog element.');
      this.element = element;
      this.trigger = options.trigger || null;
      this.initialFocus = options.initialFocus || null;
      this.showCallback = options.show || null;
      this.hideCallback = options.hide || null;
      this.onCloseCallback = options.onClose || null;
      this.onOpenCallback = options.onOpen || null;
      this.onAfterCloseCallback = options.onAfterClose || null;
      this.toggleTrigger = options.toggleTrigger || false;
      this.manageExpanded = options.manageExpanded !== false;
      this.extraFocusable = options.extraFocusable || [];
      this.inertExceptions = options.inertExceptions || [];
      this.closeDuration = options.closeDuration;
      this.closeSelector = options.closeSelector || '[data-dialog-close], [data-modal-close]';
      this.active = false;
      this.closing = false;
      this.closeTimer = null;
      this.originalDisplay = this.element.style.display;
      this.closedTabindexes = [];
      this.inerted = [];
      this.originalOverflow = null;
      this.activeElement = null;
      this.openTrigger = null;
      this.focusFrame = null;

      this.onKeydown = this.onKeydown.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onTriggerClick = (event) => {
        event.preventDefault();
        if (this.active && this.toggleTrigger) this.close();
        else this.open(this.trigger);
      };
      this.onOverlayOpen = (event) => {
        if (event.detail?.element !== this.element && (this.active || this.closing)) {
          this.close({ immediate: true, restoreFocus: false });
        }
      };

      this.element.setAttribute('role', this.element.getAttribute('role') || 'dialog');
      this.element.setAttribute('aria-modal', 'true');
      if (!this.element.hasAttribute('aria-hidden')) this.element.setAttribute('aria-hidden', 'true');
      this.element.classList.remove('is-open');
      this.element.classList.add('raidage-dialog');
      this.element.style.display = 'none';
      this.setClosedInert(true);
      this.element.addEventListener('click', this.onClick);
      document.addEventListener('raidage:overlay-open', this.onOverlayOpen);
      if (this.trigger) this.trigger.addEventListener('click', this.onTriggerClick);
    }

    getFocusableElements() {
      return [...this.extraFocusable, ...this.element.querySelectorAll(FOCUSABLE_SELECTOR)].filter((node) => {
        return node && !node.hasAttribute('hidden') && !node.closest('[inert]') &&
          node.getClientRects().length > 0 && getComputedStyle(node).visibility !== 'hidden';
      });
    }

    setClosedInert(closed) {
      if ('inert' in this.element) {
        this.element.inert = closed;
        return;
      }
      if (closed && !this.closedTabindexes.length) {
        this.closedTabindexes = Array.from(this.element.querySelectorAll(FOCUSABLE_SELECTOR))
          .map((node) => [node, node.getAttribute('tabindex')]);
        this.closedTabindexes.forEach(([node]) => node.setAttribute('tabindex', '-1'));
      } else if (!closed) {
        this.closedTabindexes.forEach(([node, value]) => {
          if (value === null) node.removeAttribute('tabindex');
          else node.setAttribute('tabindex', value);
        });
        this.closedTabindexes = [];
      }
    }

    resolveInitialFocus() {
      if (typeof this.initialFocus === 'string') {
        return this.element.querySelector(this.initialFocus);
      }
      if (typeof this.initialFocus === 'function') {
        return this.initialFocus(this.element);
      }
      if (this.initialFocus && this.element.contains(this.initialFocus)) return this.initialFocus;
      return this.getFocusableElements()[0] || this.element;
    }

    makeBackgroundInert() {
      let branch = this.element;
      while (branch && branch !== document.body) {
        const parent = branch.parentElement;
        if (!parent) break;
        Array.from(parent.children).forEach((sibling) => {
          if (sibling === branch || sibling.hasAttribute('data-raidage-dialog-open') ||
              this.inertExceptions.some((node) => node && sibling.contains(node))) return;
          if ('inert' in sibling) {
            this.inerted.push({ element: sibling, inert: sibling.inert });
            sibling.inert = true;
          } else {
            const focusableNodes = [
              ...(sibling.matches(FOCUSABLE_SELECTOR) ? [sibling] : []),
              ...sibling.querySelectorAll(FOCUSABLE_SELECTOR),
            ];
            const focusable = Array.from(focusableNodes).map((node) => [
              node,
              node.getAttribute('tabindex'),
            ]);
            this.inerted.push({
              element: sibling,
              ariaHidden: sibling.getAttribute('aria-hidden'),
              focusable,
            });
            sibling.setAttribute('aria-hidden', 'true');
            focusable.forEach(([node]) => node.setAttribute('tabindex', '-1'));
          }
        });
        branch = parent;
      }
    }

    restoreBackground() {
      this.inerted.forEach((entry) => {
        if (!entry.element.isConnected) return;
        if (Object.prototype.hasOwnProperty.call(entry, 'inert')) {
          entry.element.inert = entry.inert;
          return;
        }
        if (entry.ariaHidden === null) entry.element.removeAttribute('aria-hidden');
        else entry.element.setAttribute('aria-hidden', entry.ariaHidden);
        entry.focusable.forEach(([node, tabindex]) => {
          if (tabindex === null) node.removeAttribute('tabindex');
          else node.setAttribute('tabindex', tabindex);
        });
      });
      this.inerted = [];
    }

    open(trigger, returnFocus) {
      if (this.active) return;
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
      this.closing = false;
      this.element.classList.remove('is-closing');

      document.querySelectorAll('[data-raidage-dialog-open]').forEach((other) => {
        if (other !== this.element && other.raidageDialogInstance) {
          other.raidageDialogInstance.close({ immediate: true, restoreFocus: false });
        }
      });
      document.dispatchEvent(new CustomEvent('raidage:overlay-open', {
        detail: { source: 'dialog', element: this.element },
      }));

      // A previous dialog may have recorded this root as inert background.
      // Restore it only after that dialog has released its background state.
      this.element.style.display = this.originalDisplay;
      this.setClosedInert(false);
      // Establish the closed geometry before applying the animated open state.
      this.element.getBoundingClientRect();

      this.openTrigger = trigger || this.trigger;
      this.activeElement = returnFocus || this.openTrigger || document.activeElement;
      if (this.manageExpanded && this.openTrigger) this.openTrigger.setAttribute('aria-expanded', 'true');
      this.element.classList.add('is-open');
      this.element.setAttribute('aria-hidden', 'false');
      this.element.setAttribute('data-raidage-dialog-open', '');
      this.element.raidageDialogInstance = this;
      this.originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.makeBackgroundInert();
      if (this.showCallback) this.showCallback(this.element);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('focusin', this.onFocusIn);
      this.active = true;
      if (this.onOpenCallback) this.onOpenCallback(this.element);

      const focusTarget = this.resolveInitialFocus();
      if (focusTarget && typeof focusTarget.focus === 'function') {
        // Existing overlays animate visibility. Focus is ignored while hidden,
        // so wait for their own transition rather than changing its duration.
        const focusStarted = performance.now();
        const focusWhenVisible = () => {
          this.focusFrame = null;
          if (!this.active || !focusTarget.isConnected) return;
          if (getComputedStyle(focusTarget).visibility === 'hidden' || !focusTarget.getClientRects().length) {
            if (performance.now() - focusStarted < 1000) this.focusFrame = requestAnimationFrame(focusWhenVisible);
            return;
          }
          focusTarget.focus({ preventScroll: true });
        };
        this.focusFrame = requestAnimationFrame(focusWhenVisible);
      }
    }

    close(options = {}) {
      if (!this.active) {
        if (this.closing && options.immediate) this.finishClose();
        return;
      }
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const css = getComputedStyle(this.element);
      const durations = css.transitionDuration.split(',').map((value) => parseFloat(value) * (value.includes('ms') ? 1 : 1000));
      const duration = options.immediate || reducedMotion ? 0 : this.closeDuration ?? Math.max(0, ...durations);
      this.active = false;
      if (this.focusFrame !== null) cancelAnimationFrame(this.focusFrame);
      this.focusFrame = null;
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('focusin', this.onFocusIn);
      this.restoreBackground();
      if (this.originalOverflow !== null) document.body.style.overflow = this.originalOverflow;
      this.originalOverflow = null;

      if (options.restoreFocus !== false && this.activeElement && this.activeElement.isConnected && !this.activeElement.inert) {
        this.activeElement.focus({ preventScroll: true });
      }
      this.activeElement = null;
      if (this.manageExpanded && this.openTrigger) this.openTrigger.setAttribute('aria-expanded', 'false');
      if (this.manageExpanded && this.trigger && this.trigger !== this.openTrigger) this.trigger.setAttribute('aria-expanded', 'false');
      this.openTrigger = null;
      this.element.classList.remove('is-open');
      this.element.setAttribute('aria-hidden', 'true');
      this.setClosedInert(true);
      this.element.removeAttribute('data-raidage-dialog-open');
      delete this.element.raidageDialogInstance;
      if (this.onCloseCallback) this.onCloseCallback(this.element);
      if (duration > 0) {
        this.closing = true;
        this.element.classList.add('is-closing');
        this.closeTimer = setTimeout(() => this.finishClose(), duration + 20);
      } else this.finishClose();
    }

    finishClose() {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
      if (this.active) return;
      this.closing = false;
      this.element.classList.remove('is-closing');
      this.element.style.display = 'none';
      if (this.hideCallback) this.hideCallback(this.element);
      if (this.onAfterCloseCallback) this.onAfterCloseCallback(this.element);
    }

    onClick(event) {
      const closeControl = event.target instanceof Element ? event.target.closest(this.closeSelector) : null;
      if (closeControl && (closeControl !== this.element || event.target === this.element)) {
        event.preventDefault();
        this.close();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = this.getFocusableElements();
      if (!focusable.length) {
        event.preventDefault();
        this.element.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.element)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onFocusIn(event) {
      if (!this.element.contains(event.target) && !this.extraFocusable.some((node) => node?.contains(event.target))) {
        const focusable = this.getFocusableElements();
        (focusable[0] || this.element).focus();
      }
    }

    destroy() {
      this.close({ immediate: true, restoreFocus: false });
      this.element.removeEventListener('click', this.onClick);
      document.removeEventListener('raidage:overlay-open', this.onOverlayOpen);
      if (this.trigger) this.trigger.removeEventListener('click', this.onTriggerClick);
    }
  }

  window.RaidageDialog = RaidageDialog;
  document.dispatchEvent(new CustomEvent('raidage:dialog-ready'));
})();
